import { z } from 'zod'

function isTimeZone(value: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value })
    return true
  } catch {
    return false
  }
}

const RuntimeConfigSchema = z.object({
  API_BASE_URL: z.string().default('/api'),
  // Единый SSO хаба (hub-migration § auth-switch): origin employee-backend
  // (id.stit.tech) — куда уходит неаутентифицированный сотрудник (/auth/login|
  // logout) и куда клиент продлевает протухший access (/auth/refresh). Дефолт
  // '' — same-origin для MSW/dev. В проде — https://id.stit.tech (compose →
  // window.__APP_CONFIG__). Локального /auth у telemost больше нет.
  ID_BASE_URL: z.string().default(''),
  // Origin company-backend (api.hub.stit.tech) — второй контур сессии
  // (заказчик, cookie stit_customer): туда клиент продлевает протухший access
  // (/auth/customer/refresh) и там же гасит сессию (/auth/customer/logout).
  // Пусто = same-origin (MSW/dev). Требует апекс телемоста в COMPANY_CORS_ORIGINS.
  HUB_API_BASE_URL: z.string().default(''),
  // Единая точка входа обеих аудиторий (plan-customer 22): сотрудник уходит
  // отсюда в Authentik, заказчик вводит логин и пароль. Продукт при 401 ведёт
  // сюда, а не на id.stit.tech — по 401 он не знает, кто перед ним.
  HUB_LOGIN_URL: z.string().default('https://hub.stit.tech/login'),
  // Зона организации — зона КАЛЕНДАРНЫХ СУТОК (канон tzkit §4: та же APP_TZ, что
  // у бэкенда): период отчёта и «сегодня» обязаны иметь общие для всех границы.
  // Моменты в ней не показываются и не вводятся — это зона устройства
  // смотрящего. Невалидная IANA-зона роняет конфиг на старте: молча свалиться в
  // UTC значило бы сдвинуть границы периода и показать их как правильные.
  APP_TZ: z.string().default('Europe/Moscow').refine(isTimeZone, {
    message: 'APP_TZ must be an IANA time zone (e.g. Europe/Moscow)',
  }),
  API_MODE: z.enum(['mock', 'real']).default('mock'),
  MOCK_SCENARIO: z
    .enum(['empty', 'populated', 'authenticated', 'anonymous', 'customer', 'error'])
    .default('populated'),
  MOCK_DEVTOOLS: z.boolean().default(false),
  SENTRY_DSN: z.string().default(''),
  SENTRY_ENVIRONMENT: z.string().default('development'),
  RELEASE: z.string().default('dev'),
})

export type RuntimeConfig = z.infer<typeof RuntimeConfigSchema>

declare global {
  interface Window {
    __APP_CONFIG__?: Record<string, unknown>
  }
}

function readConfig(): RuntimeConfig {
  const fromWindow = (typeof window !== 'undefined' && window.__APP_CONFIG__) ?? {}
  const fromEnv = {
    ID_BASE_URL: import.meta.env.VITE_ID_BASE_URL,
    HUB_API_BASE_URL: import.meta.env.VITE_HUB_API_BASE_URL,
    HUB_LOGIN_URL: import.meta.env.VITE_HUB_LOGIN_URL,
    APP_TZ: import.meta.env.VITE_APP_TZ,
    API_MODE: import.meta.env.VITE_API_MODE,
    MOCK_SCENARIO: import.meta.env.VITE_MOCK_SCENARIO,
    MOCK_DEVTOOLS: import.meta.env.VITE_MOCK_DEVTOOLS === '1',
  }
  return RuntimeConfigSchema.parse({ ...fromEnv, ...fromWindow })
}

export const runtimeConfig = readConfig()
